import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../queryKeys';
import { lmsApi, type MyBatch } from '../api/lms';
import { trainerApi } from '../api/trainer';
import { adminApi } from '../api/admin';

import { useAuthStore } from '@/stores/authStore';
import { usePreviewStore } from '@/stores/previewStore';

export function useMyBatches() {
  const role = useAuthStore((s) => s.user?.role);
  const username = useAuthStore((s) => s.user?.username);
  const previewActive = usePreviewStore((s) => s.active);
  // In "preview as student" a trainer / super admin has no enrollments, so the
  // student course list is sourced from the batches they teach / administer.
  const previewSource = previewActive && (role === 'trainer' || role === 'super_admin') ? role : null;

  return useQuery({
    queryKey: [...queryKeys.lms.myBatches(), previewSource ?? 'own'],
    queryFn: async (): Promise<MyBatch[]> => {
      if (previewSource === 'trainer') {
        const { data } = await trainerApi.listBatches();
        return (data ?? []).map((b) => ({
          id: b.id,
          name: b.name,
          trainer_name: username ?? '',
          total_topics: 0,
          completed_topics: 0,
          progress_pct: 0,
        }));
      }
      if (previewSource === 'super_admin') {
        const batches = await adminApi.listBatches();
        return batches.map((b) => ({
          id: b.id,
          name: b.name,
          trainer_name: b.trainers?.[0]?.username ?? '',
          total_topics: 0,
          completed_topics: 0,
          progress_pct: 0,
        }));
      }
      const { data } = await lmsApi.listMyBatches();
      return data ?? [];
    },
  });
}

export function useBatchCurriculum(batchId: string) {
  return useQuery({
    queryKey: queryKeys.lms.batchCurriculum(batchId),
    queryFn: async () => {
      const { data } = await lmsApi.getBatchCurriculum(batchId);
      return data;
    },
    enabled: !!batchId,
  });
}

export function useTopicDetail(topicId: string) {
  return useQuery({
    queryKey: queryKeys.lms.topicDetail(topicId),
    queryFn: async () => {
      const { data } = await lmsApi.getTopicDetail(topicId);
      return data;
    },
    enabled: !!topicId,
  });
}

export function useCompleteTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (topicId: string) => lmsApi.completeTopic(topicId),
    onSuccess: (_data, topicId) => {
      qc.invalidateQueries({ queryKey: queryKeys.lms.all });
      qc.invalidateQueries({ queryKey: queryKeys.lms.topicDetail(topicId) });
    },
  });
}

export function useUncompleteTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (topicId: string) => lmsApi.uncompleteTopic(topicId),
    onSuccess: (_data, topicId) => {
      qc.invalidateQueries({ queryKey: queryKeys.lms.all });
      qc.invalidateQueries({ queryKey: queryKeys.lms.topicDetail(topicId) });
    },
  });
}

export function useCreateNote(topicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => lmsApi.createNote(topicId, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.lms.topicDetail(topicId) });
    },
  });
}

export function useSubmitFeedback(topicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rating, comment }: { rating: number; comment: string }) =>
      lmsApi.submitFeedback(topicId, rating, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.lms.topicDetail(topicId) });
    },
  });
}

export function useLeaderboard(batchId: string, scope: 'batch' | 'course' = 'batch') {
  return useQuery({
    queryKey: [...queryKeys.lms.leaderboard(batchId), scope],
    queryFn: async () => {
      const { data } = scope === 'course'
        ? await lmsApi.getCourseLeaderboard(batchId)
        : await lmsApi.getLeaderboard(batchId);
      return data ?? null;
    },
    enabled: !!batchId,
  });
}

// Leaderboard ranks are inherently per-batch (there's no server-side "all
// batches" aggregate the way useHome/useUpcoming have) — a single merged
// ranking across batches wouldn't mean anything. "All Courses" mode instead
// fetches every enrolled batch's leaderboard so the page can render one
// section per batch.
export function useLeaderboardMultiple(batchIds: string[]) {
  return useQueries({
    queries: batchIds.map((batchId) => ({
      queryKey: queryKeys.lms.leaderboard(batchId),
      queryFn: async () => {
        const { data } = await lmsApi.getLeaderboard(batchId);
        return data ?? null;
      },
      enabled: !!batchId,
    })),
  });
}

export function usePractice(batchId: string) {
  return useQuery({
    queryKey: queryKeys.lms.practice(batchId),
    queryFn: async () => {
      const { data } = await lmsApi.getPractice(batchId);
      return data;
    },
    enabled: !!batchId,
  });
}

export function usePracticeAssessment(assessmentId: string) {
  return useQuery({
    queryKey: queryKeys.lms.practiceAssessment(assessmentId),
    queryFn: async () => {
      const { data } = await lmsApi.getPracticeAssessment(assessmentId);
      return data;
    },
    enabled: !!assessmentId,
  });
}

/**
 * Calendar feed for a date window. `batchId` may be 'all'. Pass ISO strings so
 * the query key is stable across renders — Date objects would never match.
 */
export function useSchedule(batchId: string, from: string, to: string) {
  return useQuery({
    queryKey: queryKeys.lms.schedule(batchId, from, to),
    queryFn: async () => {
      const { data } = await lmsApi.getSchedule(batchId, from, to);
      return data ?? [];
    },
    enabled: !!batchId,
  });
}

export function useUpcoming(batchId: string) {
  return useQuery({
    queryKey: queryKeys.lms.upcoming(batchId),
    queryFn: async () => {
      const { data } = await lmsApi.getUpcoming(batchId);
      return data ?? [];
    },
    enabled: !!batchId,
  });
}
